import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { navLinks } from '../data/mockData';
import { useCMS } from '../context/CMSContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { get } = useCMS();
  const company = get('company_info');
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative text-sm font-medium transition-colors duration-200 ${
      isActive ? 'text-white' : 'text-white/60 hover:text-white'
    }`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'backdrop-blur-xl bg-[#050914]/80 border-b border-white/5'
            : 'backdrop-blur-md bg-[#050914]/30 border-b border-transparent'
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo" aria-label="Digitize home">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_digitize-pro-2/artifacts/bzn1ek7z_image.png"
                alt="Digitize Intl."
                className="h-24 md:h-32 w-auto object-contain brightness-0 invert -my-8"
                loading="eager"
                decoding="async"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <NavLink
                  key={link.name}
                  to={link.href}
                  end
                  className={linkClass}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {({ isActive }) => (
                    <>
                      {link.name}
                      {isActive && (
                        <span className="absolute -bottom-2 left-0 right-0 h-px bg-[#FF6B35]" aria-hidden />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#050914] text-sm font-semibold hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                data-testid="nav-cta-book"
              >
                Book a Consultation
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </Link>
            </div>

            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle navigation"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden bg-[#050914]/95 backdrop-blur-2xl pt-24 px-6" data-testid="nav-mobile-drawer">
          <nav className="flex flex-col gap-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.href}
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-2xl font-display ${isActive ? 'text-white' : 'text-white/80 hover:text-white'}`
                }
                data-testid={`nav-mobile-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-[#050914] font-semibold"
              data-testid="nav-mobile-cta-book"
            >
              Book a Consultation
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;
